import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import { Edit, CreditCard, Save, Cancel } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { updatePaymentMethod, getPaymentMethod } from '../services/paymentService';

const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripeKey ? loadStripe(stripeKey) : null;

const CardForm = ({ onSave, onCancel, loading, organizationId }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState('');
  const [cardError, setCardError] = useState('');
  const { enqueueSnackbar } = useSnackbar();

  // Check if Stripe is loaded
  if (!stripePromise) {
    return (
      <Alert severity="error">
        Stripe is not configured. Please add REACT_APP_STRIPE_PUBLISHABLE_KEY to your .env file.
      </Alert>
    );
  }

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    const card = elements.getElement(CardElement);
    if (!card) {
      setError('Card element not found');
      return;
    }

    setError('');
    setCardError('');

    try {
      // Create payment method
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: card,
      });

      if (stripeError) {
        setCardError(stripeError.message);
        return;
      }

      // Update payment method on backend
      await updatePaymentMethod(organizationId, paymentMethod.id);
      enqueueSnackbar('Payment method updated successfully!', { variant: 'success' });
      onSave(paymentMethod);
    } catch (error) {
      console.error('Error updating payment method:', error);
      const errorMessage = error.response?.data?.error || 'Failed to update payment method';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
    },
    hidePostalCode: true,
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Enter your new payment method details:
      </Typography>

      <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
        <CardElement
          options={cardElementOptions}
          onChange={(event) => {
            setCardError(event.error ? event.error.message : '');
          }}
        />
      </Box>

      {cardError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {cardError}
        </Alert>
      )}

      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={loading}
          startIcon={<Cancel />}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={!stripe || loading}
          startIcon={loading ? <CircularProgress size={16} /> : <Save />}
        >
          {loading ? 'Saving...' : 'Save Payment Method'}
        </Button>
      </Box>
    </Box>
  );
};

const PaymentMethodEditor = ({ organizationId, currentPaymentMethod, onPaymentMethodUpdated }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(currentPaymentMethod);

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    setPaymentMethod(currentPaymentMethod);
  }, [currentPaymentMethod]);

  // Check if Stripe is properly configured
  if (!stripeKey) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Payment Method
          </Typography>
          <Alert severity="error">
            Stripe is not configured. Please add REACT_APP_STRIPE_PUBLISHABLE_KEY to your .env file.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const handleSave = async (newPaymentMethod) => {
    setLoading(true);
    try {
      // The save operation is handled in CardForm, just update local state
      setPaymentMethod(newPaymentMethod);
      setDialogOpen(false);
      onPaymentMethodUpdated?.(newPaymentMethod);
    } catch (error) {
      console.error('Error in handleSave:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setDialogOpen(false);
    setLoading(false);
  };

  const formatCardBrand = (brand) => {
    const brandMap = {
      visa: 'Visa',
      mastercard: 'Mastercard',
      amex: 'American Express',
      discover: 'Discover',
      diners: 'Diners Club',
      jcb: 'JCB',
      unionpay: 'UnionPay',
    };
    return brandMap[brand] || brand?.toUpperCase() || 'Unknown';
  };

  return (
    <>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Payment Method
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your default payment method for subscription billing.
          </Typography>

          <Box display="flex" alignItems="center" gap={2}>
            <Box sx={{ flex: 1 }}>
              {paymentMethod?.card ? (
                <Box display="flex" alignItems="center" gap={2}>
                  <CreditCard color="action" />
                  <Box>
                    <Typography variant="body1">
                      {formatCardBrand(paymentMethod.card.brand)} ending in {paymentMethod.card.last4}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Expires {paymentMethod.card.exp_month?.toString().padStart(2, '0')}/{paymentMethod.card.exp_year}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  No payment method on file
                </Typography>
              )}
            </Box>
            
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={() => setDialogOpen(true)}
              sx={{ minWidth: 140 }}
            >
              Update Card
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={handleCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Update Payment Method</DialogTitle>
        <DialogContent>
          {stripePromise ? (
            <Elements stripe={stripePromise}>
              <CardForm
                onSave={handleSave}
                onCancel={handleCancel}
                loading={loading}
                organizationId={organizationId}
              />
            </Elements>
          ) : (
            <Alert severity="error">
              Stripe is not configured. Please add REACT_APP_STRIPE_PUBLISHABLE_KEY to your .env file.
            </Alert>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PaymentMethodEditor; 